import { TextFields as TextFieldsIcon } from '@mui/icons-material';
import { Box, FormHelperText, IconButton, InputAdornment, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { EAttributeType, EAttributeUnit, EAttributeUnitFamily, INumberValidation, ISelectionValidation, ITextValidation } from '../../../../@types/AttributeTypes';
import { EUnitType } from '../../../../@types/Enums';
import { IconAttribute } from '../../../../assets/icons';
import AttributeModel from '../../../../models/standard/AttributeModel';
import { usePlan } from '../../../../provider/PlanProvider';
import { useUnitContext } from '../../../../provider/UnitProvider';
import {
  attributeUnitDisplayMap,
  convertAttributeValue,
  getCorrespondingUnit,
  getLocalizedAttributeLabel,
  IMPERIAL_TO_METRIC,
  METRIC_SCALE_FACTORS,
  METRIC_TO_IMPERIAL,
  unitFamilyToUnitRecord
} from '../../../../utils/attributes/attributeUtils';
import { coerceSelectionValueForStorage, getDefaultSelectionValue, getSelectionOptions, SelectionValue, serializeSelectionValue } from '../../../../utils/attributes/selectionAttributeUtils';

interface AttachmentAttributeContentProps {
  data: AttributeModel;
  onUpdate: (newValue: string) => void;
  onUnitChange?: (newUnit: string) => void;
}

export const AttachmentAttributeContent: React.FC<AttachmentAttributeContentProps> = ({ data, onUpdate, onUnitChange }) => {
  const intl = useIntl();
  const { locale } = intl;
  const { unitType } = useUnitContext();
  const { planModel } = usePlan();

  const currentLocale = locale.split('-')[0];
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';
  const isRegionUS = regionCode === 'US';
  const defaultsRegionKey = isRegionUS ? 'US' : 'DE';

  const [editing, setEditing] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [showTextField, setShowTextField] = useState<boolean>(false);

  const [localDisplayUnit, setLocalDisplayUnit] = useState<EAttributeUnit | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  // --- 1. RESOLVE CONFIGURATION ---
  const validationObj = data.validation?.[defaultsRegionKey] || data.validation?.GLOBAL || {};
  const defaultObj = data.defaults?.[defaultsRegionKey] || data.defaults?.GLOBAL;

  const numValidation = validationObj as INumberValidation;
  const textValidation = validationObj as ITextValidation;
  const selValidation = validationObj as ISelectionValidation;

  const isStorageMetric = !isRegionUS;
  const isDisplayMetric = unitType === EUnitType.METRIC;
  const isNativeMode = isStorageMetric === isDisplayMetric;

  // --- UNIT RESOLUTION ---
  let storedUnit = data.unit;
  if (!storedUnit) {
    storedUnit = numValidation.unit || defaultObj?.unit;
  }

  let activeDisplayUnitKey = localDisplayUnit || (storedUnit as EAttributeUnit);

  // Force conversion if in Foreign Mode and no local override
  if (!localDisplayUnit && !isNativeMode && storedUnit) {
    activeDisplayUnitKey = getCorrespondingUnit(storedUnit as EAttributeUnit, !isDisplayMetric);
  }

  const displayUnitLabel = activeDisplayUnitKey ? attributeUnitDisplayMap[activeDisplayUnitKey] || activeDisplayUnitKey : '';

  // --- 2. INITIALIZATION / CALCULATION ---
  let effectiveVal = data.value;

  if (effectiveVal === undefined || effectiveVal === null || effectiveVal === '') {
    if (data.type === EAttributeType.BOOL) {
      effectiveVal = defaultObj?.value !== undefined && defaultObj.value !== null && String(defaultObj.value) !== '' ? String(defaultObj.value) : 'true';
    } else if (data.type === EAttributeType.SELECTION) {
      const options = getSelectionOptions(selValidation.values as SelectionValue[] | undefined);
      const defaultValue = getDefaultSelectionValue(defaultObj?.value, options);

      if (defaultValue !== null) {
        effectiveVal = serializeSelectionValue(defaultValue);
      }
    } else if (defaultObj?.value !== undefined && defaultObj?.value !== null) {
      effectiveVal = String(defaultObj.value);
    }
  }

  const rawVal = effectiveVal !== undefined && effectiveVal !== null ? String(effectiveVal) : '';

  useEffect(() => {
    if (rawVal !== '') setShowTextField(true);
  }, [rawVal]);

  // Calculate Display Value
  const precision = numValidation.precision ?? 3;
  let calculatedDisplayValue = rawVal;

  if (!isNativeMode && data.type === EAttributeType.NUMBER && rawVal && !isNaN(Number(rawVal)) && activeDisplayUnitKey && storedUnit) {
    const num = Number(rawVal);

    // 1. Basic Conversion (Result is Imperial OR Metric Base/Meters)
    let converted = convertAttributeValue(num, storedUnit as EAttributeUnit, !isDisplayMetric);

    // 2. MAGNITUDE FIX (Imperial Storage -> Metric Display)
    if (isDisplayMetric && IMPERIAL_TO_METRIC[storedUnit as EAttributeUnit]) {
      const scale = METRIC_SCALE_FACTORS[activeDisplayUnitKey] || 1;
      converted = converted / scale;
    }

    const roundFactor = Math.pow(10, precision);
    calculatedDisplayValue = String(Math.round(converted * roundFactor) / roundFactor);
  } else if (isNativeMode && data.type === EAttributeType.NUMBER && rawVal && !isNaN(Number(rawVal)) && activeDisplayUnitKey && storedUnit && activeDisplayUnitKey !== storedUnit) {
    // For now, intra-system conversion (CM->MM) is not fully automated here, assumes rawVal is correct or handled by parent.
    // Usually calculatedDisplayValue = rawVal is correct if parent didn't convert it.
    calculatedDisplayValue = rawVal;
  }

  // FIX: Sync localValue with calculatedDisplayValue when units change
  // This ensures the input field updates immediately when you toggle Metric/Imperial
  useEffect(() => {
    setLocalValue(calculatedDisplayValue);
  }, [calculatedDisplayValue]);

  const valueToShow = editing ? localValue : calculatedDisplayValue;
  const isChecked = rawVal === 'true';
  const displayName = getLocalizedAttributeLabel(data, locale);

  // --- 3. AVAILABLE UNITS ---
  let availableUnits: EAttributeUnit[] = [];
  const isNoneFamily = !data.unitFamily || data.unitFamily === EAttributeUnitFamily.NONE;

  // --- NEW LOGIC: Lock the unit if it is strictly defined in validation or defaults ---
  const isUnitLocked = !!(numValidation.unit || defaultObj?.unit);

  if (isNativeMode && data.type === EAttributeType.NUMBER && !isUnitLocked) {
    const familyToUse = isNoneFamily ? EAttributeUnitFamily.NONE : data.unitFamily!;
    const rawUnits = unitFamilyToUnitRecord[familyToUse] || [];
    if (unitType === EUnitType.METRIC) {
      availableUnits = rawUnits.filter((u) => Object.prototype.hasOwnProperty.call(METRIC_TO_IMPERIAL, u));
    } else {
      availableUnits = rawUnits.filter((u) => Object.prototype.hasOwnProperty.call(IMPERIAL_TO_METRIC, u));
    }
    if (availableUnits.length > 0 && (isNoneFamily || !activeDisplayUnitKey)) {
      availableUnits.unshift('' as EAttributeUnit);
    }
  }

  let dropdownValue = activeDisplayUnitKey || '';
  if (availableUnits.length > 0 && activeDisplayUnitKey && !availableUnits.includes(activeDisplayUnitKey)) {
    dropdownValue = availableUnits.includes(activeDisplayUnitKey) ? activeDisplayUnitKey : availableUnits[0];
  }

  // --- 4. SCROLL BLOCKER ---
  useEffect(() => {
    const el = inputRef.current;
    if (el && editing) {
      const preventScroll = (e: WheelEvent) => {
        e.preventDefault();
        // Do NOT stop propagation here!
      };
      el.addEventListener('wheel', preventScroll, { passive: false });
      return () => el.removeEventListener('wheel', preventScroll);
    }
  }, [editing]);

  // --- HANDLERS ---

  const handleInputFocus = () => {
    setEditing(true);
    setLocalValue(calculatedDisplayValue);
    setError(null);
  };

  const saveNumberToParent = (valStr: string) => {
    const num = parseFloat(valStr);
    let valueToSave = valStr;

    // Convert Display -> Storage if needed
    if (displayUnitLabel && storedUnit && activeDisplayUnitKey && activeDisplayUnitKey !== storedUnit) {
      if (!isNaN(num)) {
        const roundFactor = Math.pow(10, precision);
        let converted = num;

        if (!isNativeMode) {
          // Metric Display -> Imperial Storage
          if (isStorageMetric === false && isDisplayMetric === true) {
            // 1. Displayed CM -> Base Meters
            // convertAttributeValue handles the base conversion, but we need to account for CM->M magnitude first if using generic helper.
            // Easier way: Use the reverse logic from display calculation.

            // Display (CM) -> Base (M)
            const scale = METRIC_SCALE_FACTORS[activeDisplayUnitKey] || 1;
            const meters = num * scale;

            // Base (M) -> Storage (IN)
            // Since M -> IN is a standard conversion, we can use the helper:
            // convertAttributeValue(meters, M, true) -> IN
            converted = convertAttributeValue(meters, EAttributeUnit.M, true);
          }
          // Imperial Display -> Metric Storage
          else if (isStorageMetric === true && isDisplayMetric === false) {
            // Display (IN) -> Base (M)
            const meters = convertAttributeValue(num, activeDisplayUnitKey, false);

            // Base (M) -> Storage (MM/CM)
            // Divide by storage unit scale (e.g. 0.001 for MM)
            const scale = METRIC_SCALE_FACTORS[storedUnit as EAttributeUnit] || 1;
            converted = meters / scale;
          }
          // General Fallback (e.g. Temp)
          else {
            converted = convertAttributeValue(num, activeDisplayUnitKey, isRegionUS);
          }
        }

        valueToSave = String(Math.round(converted * roundFactor) / roundFactor);
      }
    }
    onUpdate(valueToSave);
  };

  const handleInputBlur = () => {
    if (data.type !== EAttributeType.NUMBER) {
      setEditing(false);
      setLocalValue('');
      setError(null);
      return;
    }

    const valStr = localValue.replace(',', '.');
    let num = parseFloat(valStr);
    let clamped = false;

    if (!isNaN(num)) {
      if (numValidation.min !== undefined && numValidation.min !== null && num < numValidation.min) {
        num = numValidation.min;
        clamped = true;
      } else if (numValidation.max !== undefined && numValidation.max !== null && num > numValidation.max) {
        num = numValidation.max;
        clamped = true;
      }
    }

    if (clamped) {
      const factor = Math.pow(10, precision);
      num = Math.round(num * factor) / factor;
      const newStr = String(num);
      saveNumberToParent(newStr);
    }

    setEditing(false);
    setLocalValue('');
    setError(null);
  };

  const handleNumberChange = (raw: string) => {
    const val = raw.replace(',', '.');
    const allowNegative = numValidation.min !== undefined && numValidation.min < 0;

    if (val === '') {
      setLocalValue('');
      setError(null);
      onUpdate('');
      return;
    }
    if (val === '-' || val === '.') {
      if (val === '-' && !allowNegative) return;
      setLocalValue(val);
      return;
    }

    const negativePattern = allowNegative ? '-?' : '';
    const regex = new RegExp(`^${negativePattern}(0|[1-9]\\d*)(\\.\\d{0,${precision}})?$`);

    if (!val.endsWith('.') && !regex.test(val)) return;

    setLocalValue(val);

    const num = parseFloat(val);
    let errorMsg = null;
    if (!isNaN(num)) {
      if (numValidation.min !== undefined && num < numValidation.min) errorMsg = `Min: ${numValidation.min}`;
      else if (numValidation.max !== undefined && num > numValidation.max) errorMsg = `Max: ${numValidation.max}`;
    }

    if (errorMsg) {
      setError(errorMsg);
      return;
    }

    setError(null);
    saveNumberToParent(val);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalValue(val);
    const maxLength = textValidation.maxLength ?? 150;
    let errorMsg = null;

    if (val.length > maxLength) return;

    if (textValidation.minLength && val.length > 0 && val.length < textValidation.minLength) {
      errorMsg = `Min length: ${textValidation.minLength}`;
    }
    if (textValidation.pattern) {
      try {
        const regex = new RegExp(textValidation.pattern);
        if (val !== '' && !regex.test(val)) {
          errorMsg = 'Invalid format';
        }
      } catch (e) {
        console.error('Invalid Regex', e);
      }
    }
    setError(errorMsg);

    if (!errorMsg) {
      onUpdate(val);
    }
  };

  const handleStepChange = (delta: number) => {
    const num = parseFloat(valueToShow);
    const startVal = isNaN(num) ? numValidation.min ?? 0 : num;
    const step = numValidation.step ?? 1;
    const newValue = startVal + delta * step;

    const roundFactor = Math.pow(10, precision);
    const roundedValue = Math.round(newValue * roundFactor) / roundFactor;

    handleNumberChange(String(roundedValue));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      handleStepChange(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      handleStepChange(-1);
    }
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (document.activeElement === e.target) {
      // Stop bubbling here so React handles it
      e.stopPropagation();
      e.preventDefault();

      const delta = e.deltaY < 0 ? 1 : -1;
      handleStepChange(delta);
    }
  };

  const handleBoolToggle = (checked: boolean) => {
    onUpdate(checked ? 'true' : 'false');
  };

  const handleUnitSelect = (newUnit: string) => {
    setLocalDisplayUnit(newUnit as EAttributeUnit);
    if (onUnitChange) {
      onUnitChange(newUnit);
    }
  };

  // Logic to decide if we show Dropdown or Static Label
  // Note: We allow dropdown even if onUnitChange is missing, to update local state at least
  const showDropdown = isNativeMode && availableUnits.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: '#009fe3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': { width: '26px', height: '26px' },
            '& svg path': { fill: '#ffffff !important' },
          }}
        >
          <IconAttribute />
        </Box>

        <Typography
          variant="body1"
          sx={{
            flex: '0 0 90px',
            width: 120,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.2,
            color: '#464646',
            fontSize: 16,
          }}
        >
          {displayName}:
        </Typography>

        {/* BOOLEAN UI */}
        {data.type === EAttributeType.BOOL && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Switch
              checked={isChecked}
              disabled={data.forced}
              onChange={(_, checked) => handleBoolToggle(checked)}
              size="small"
              sx={{
                width: 36,
                height: 32,
                top: 6,
                padding: 0,
                '& .MuiSwitch-switchBase': {
                  padding: 0,
                  top: 2,
                  left: 2,
                  '&.Mui-checked + .MuiSwitch-track': { backgroundColor: '#464646', opacity: 1 },
                },
                '& .MuiSwitch-thumb': { width: 16, height: 16, backgroundColor: '#fff' },
                '& .MuiSwitch-track': { borderRadius: 10, height: 20, backgroundColor: '#464646', opacity: 1 },
              }}
            />
            <Typography variant="body2" sx={{ ml: 0.5, color: data.forced ? '#BABABA' : undefined }}>
              {isChecked ? intl.formatMessage({ id: 'AttributeList.yes', defaultMessage: 'Ja' }) : intl.formatMessage({ id: 'AttributeList.no', defaultMessage: 'Nein' })}
            </Typography>
          </Box>
        )}

        {/* NUMBER UI */}
        {data.type === EAttributeType.NUMBER && (
          <TextField
            variant="standard"
            size="small"
            type="text"
            inputMode="decimal"
            inputRef={inputRef}
            disabled={data.forced}
            error={!!error}
            value={valueToShow}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onChange={(e) => handleNumberChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onWheel={handleWheel}
            placeholder={intl.formatMessage({ id: 'AttributeList.attributeNumberValuePlaceholder', defaultMessage: 'Enter number value' })}
            InputProps={{
              endAdornment: showDropdown ? (
                <InputAdornment position="end">
                  <Select
                    variant="standard"
                    disableUnderline
                    value={dropdownValue}
                    onChange={(e) => handleUnitSelect(e.target.value as string)}
                    sx={{
                      '& .MuiSelect-select': {
                        color: '#464646',
                        fontSize: '0.9rem',
                        paddingRight: '24px !important',
                        textAlign: 'right',
                        '&:focus': { backgroundColor: 'transparent' },
                      },
                      '& .MuiSvgIcon-root': { right: 0 },
                    }}
                  >
                    {availableUnits.map((u) => (
                      <MenuItem key={u} value={u}>
                        {u === '' ? intl.formatMessage({ id: 'AttributeList.none', defaultMessage: 'None' }) : attributeUnitDisplayMap[u as EAttributeUnit] || u}
                      </MenuItem>
                    ))}
                  </Select>
                </InputAdornment>
              ) : (
                displayUnitLabel && (
                  <InputAdornment position="end">
                    <Typography sx={{ fontSize: '0.9rem' }}>{displayUnitLabel}</Typography>
                  </InputAdornment>
                )
              ),
            }}
            sx={{
              ml: 1,
              flex: 1,
              '& .MuiInputBase-input': { py: 0.5, color: 'inherit', fontSize: '0.9rem' },
              '& .MuiInput-underline:before': { borderBottom: '1px solid #BABABA' },
            }}
          />
        )}

        {/* SELECTION UI */}
        {data.type === EAttributeType.SELECTION &&
          (() => {
            const options = getSelectionOptions(selValidation.values as SelectionValue[] | undefined);
            const selectValue = getSelectionValueForSelect(rawVal, options);

            return (
              <Select
                value={selectValue}
                onChange={(e) => onUpdate(coerceSelectionValueForStorage(e.target.value))}
                variant="standard"
                displayEmpty
                disabled={data.forced}
                sx={{ ml: 1, flex: 1, fontSize: '0.9rem', '&:before': { borderBottom: '1px solid #BABABA' } }}
              >
                <MenuItem value="" disabled>
                  <Typography variant="caption" color="textSecondary">
                    {intl.formatMessage({ id: 'AttributeList.select', defaultMessage: 'Select...' })}
                  </Typography>
                </MenuItem>

                {options.map((opt) => {
                  const optionValue = serializeSelectionValue(opt);

                  return (
                    <MenuItem key={optionValue} value={optionValue}>
                      {getLocalizedSelectionLabel(opt, locale)}
                    </MenuItem>
                  );
                })}
              </Select>
            );
          })()}

        {/* TEXT UI */}
        {data.type === EAttributeType.TEXT && (
          <>
            {!showTextField && rawVal === '' && !data.forced ? (
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                <IconButton size="small" sx={{ color: '#464646' }} onClick={() => setShowTextField(true)}>
                  <TextFieldsIcon />
                </IconButton>
              </Box>
            ) : (
              <TextField
                variant="standard"
                size="small"
                type="text"
                value={editing ? localValue : rawVal}
                disabled={data.forced}
                error={!!error}
                onFocus={() => {
                  setEditing(true);
                  setLocalValue(rawVal);
                }}
                onBlur={handleInputBlur}
                onChange={handleTextChange}
                placeholder={intl.formatMessage({ id: 'AttributeList.attributeValuePlaceholder', defaultMessage: 'Enter value' })}
                inputProps={{ maxLength: textValidation.maxLength ?? 150 }}
                sx={{ ml: 0, flex: 1, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.9rem' }, '& .MuiInput-underline:before': { borderBottom: '1px solid #BABABA' } }}
              />
            )}
          </>
        )}
      </Box>

      {/* Error Message Row */}
      {error && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', paddingLeft: '90px' }}>
          <FormHelperText error sx={{ mt: 0, fontSize: '0.75rem', textAlign: 'center' }}>
            {error}
          </FormHelperText>
        </Box>
      )}
    </Box>
  );
};
function getSelectionValueForSelect(rawVal: string, options: SelectionValue[]) {
  throw new Error('Function not implemented.');
}

function getLocalizedSelectionLabel(opt: SelectionValue, locale: string): React.ReactNode {
  throw new Error('Function not implemented.');
}
